import { useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { IApiPagable, IChangePagable, IPagable, IUnChangePagable } from "../services/IPagable";
import useSchemaSearchParams, { TUrlSchemaKeys } from "./useSerarchParams";

export type IOnSort = (sort: IPagable["sort"], sortDirection?: IPagable["sortDirection"]) => void;

export interface IUsePagable<T> {
  initPagable: Partial<IPagable>,
  filterMap?: T, // If filter map changes - reset pagable params
  allowUrlFilterArr?: string[],
  setFilterParams?: (filterMap: Partial<T>) => unknown,
}

const defaultPagable: IChangePagable = {
  page: 1,
  size: 50,
  sort: '',
  sortDirection: '',
}

export default function usePagable <T>(pagableProps?: IUsePagable<T>) {
  const { ref, inView } = useInView({ threshold: 0 }); 
  const params = useSchemaSearchParams();

  // -- Computed pagable
  const [pagable, setPagable] = useState<IPagable & T | IPagable>();

  // -- Editable page parameters
  const [pagableState, setPagableState] = useState<IChangePagable & T | IChangePagable>(); 

  // --
  // -- Sync up state with url
  // --
  const isUrlAppliedRef = useRef(false);

  // -- URL -> State
  useEffect(() => {      
    if (!isUrlAppliedRef.current) {
      isUrlAppliedRef.current = true; 
      const currentPagable = params.getParamsByKeys(Object.keys(pagableProps?.initPagable || {})) as IChangePagable;
      const currentFilter = params.getParamsByKeys(pagableProps?.allowUrlFilterArr || []);
    
      pagableProps?.setFilterParams
      && Object.keys(currentFilter)?.length
      && pagableProps?.setFilterParams(currentFilter);

      (Object.keys(currentPagable)?.length || Object.keys(currentFilter)?.length)
      && setPagableState(() => ({
        ...(pagableProps?.filterMap || {}),
        ...currentPagable as IChangePagable,
        ...currentFilter as T,
      }));           
    }
  }, []);

  // -- State -> URL
  useEffect(() => {
    isUrlAppliedRef.current && pagableState && params.setMap(pagableState as Record<TUrlSchemaKeys, string | number>)
  }, [pagableState])



  useEffect(() => {
    if (isUrlAppliedRef.current) {      
      // !!pagableProps?.filterMap && 
      setPagableState(() => ({      
        ...defaultPagable,      
        ...(pagableProps?.initPagable || {}),
        ...(pagableProps?.filterMap || {}),
      }));
    }
  }, [pagableProps?.filterMap]);


  // --
  // -- Pagable parameters from API
  // --
  const [pagableApiState, setPagableApiState] = useState<IUnChangePagable>();
  const updatePagableApiState = (data?: IApiPagable<any>) => 
    data && setPagableApiState(() => ({
      offset: data.offset,
      totalElements: data.totalElements,
      totalPages: data.totalPages,
    }));

  // -- To the Next page
  const nextPage = () => pagable
    && pagableApiState?.totalPages
    && pagable.page < pagableApiState.totalPages
    && setPagableState((prevPagable) => ({
      ...defaultPagable,
      ...(prevPagable || {}),
      page: (prevPagable?.page || 0) + 1,
    }));

  // -- To the First page
  const firstPage = () => 
    setPagableState((prevPagable) => ({
      ...defaultPagable,
      ...prevPagable,
      page: 1,
    }));

  // -- Go to the next page when scroll to the observer
  useEffect(() => { nextPage(); }, [inView]);

  const extractPagableFromApi = (source?: Partial<IPagable | IApiPagable<any>>): Partial<IPagable> => {
    const { content, ...pagable } = (source || {}) as IApiPagable<any>;
    return pagable || {};
  }    

  const updatePagable = (newPagable?: Partial<IPagable | IApiPagable<any>>) => {      
    const extractedPagable = extractPagableFromApi(newPagable);

    pagable && setPagableState((prevPagable) => ({
      ...defaultPagable,
      ...prevPagable,
      ...extractedPagable,
    }));
  }

  // -- Sorting
  const onSort: IOnSort = (sort, sortDirection) => {    
    pagableState && setPagableState(() => ({
      ...pagableState,
      page: 1,
      sort,
      sortDirection: sortDirection
        ? sortDirection
        : pagableState?.sort === sort && pagableState?.sortDirection === 'asc'
            ? 'desc'
            : 'asc',
    }));
  }

  // -- Connect result pagable to api pagable and pagable state
  useEffect(() => {
    pagableState && pagableApiState && setPagable((prevState) => ({
      ...prevState,
      ...pagableState,
      ...pagableApiState,
    }));
  }, [pagableState, pagableApiState]);

  return {
    loadTriggerComponent: <div className="h-2" ref={ref} />, 
    onSort,
    updatePagable,
    pagableApiState,
    updatePagableApiState,
    setPagable,
    pagableState,
    pagable,
    firstPage,
    nextPage,
  }
}